import { useState, useEffect } from 'react';
import { Download, X, Share } from 'lucide-react';

function InstallAppButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // For development/testing, always show button
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      setIsInstallable(true);
      setIsIOS(iOS);
    }

    // Android install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstallable(false);
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
  };

  if (isInstalled) {
    return null;
  }

  if (!isInstallable && !isIOS) {
    return null;
  }

  return (
    <>
      <button
        onClick={handleInstallClick}
        data-testid="install-app-button"
        className="fixed bottom-20 right-4 bg-[#E11D48] hover:bg-[#BE123C] text-white p-4 rounded-full shadow-2xl transition-all hover:scale-110 z-40 flex items-center gap-2"
      >
        <Download className="w-5 h-5" />
        <span className="font-bold text-sm uppercase tracking-wider pr-1">Install App</span>
      </button>

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-end justify-center z-50 p-6">
          <div className="bg-zinc-900 border-2 border-[#E11D48] rounded-lg max-w-md w-full p-6 animate-in slide-in-from-bottom">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Anton'] text-2xl text-white uppercase tracking-tight">
                Install App
              </h3>
              <button
                onClick={() => setShowIOSInstructions(false)}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4 text-zinc-300">
              <p className="text-sm">To install Long Island on your iPhone:</p>
              
              <ol className="space-y-3 text-sm">
                <li className="flex gap-3">
                  <span className="text-[#E11D48] font-bold">1.</span>
                  <span>
                    Tap the <Share className="w-4 h-4 inline mx-1" /> <strong>Share</strong> button at the bottom of your browser
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#E11D48] font-bold">2.</span>
                  <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#E11D48] font-bold">3.</span>
                  <span>Tap <strong>"Add"</strong> in the top right corner</span>
                </li>
              </ol>

              <p className="text-xs text-zinc-500 pt-2">
                The app will open in fullscreen mode like a native app!
              </p>
            </div>

            <button
              onClick={() => setShowIOSInstructions(false)}
              className="w-full mt-6 bg-[#E11D48] hover:bg-[#BE123C] text-white font-bold py-3 uppercase tracking-wider transition-colors"
            >
              Got It
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default InstallAppButton;
